// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useState } from 'react';

import type { EvolveApiResponse, EvolveApiRoute, PageFetcher, AdditionalRequestConfig } from '@evolvemep/api-ts';

import { useRemountKey } from 'hooks/useRemountKey';

const getAllDataFromFetcherImpl = async <
  Route extends EvolveApiRoute<'GET_PAGINATED'>,
  Response extends EvolveApiResponse<'GET_PAGINATED', Route>,
>(
  pageFetcher: PageFetcher<Route>,
  config: AdditionalRequestConfig<'GET_PAGINATED', Route>,
  existingData: Response['data'] = [],
): Promise<Response['data']> => {
  const { entireCount, data } = await pageFetcher(
    { skip: existingData.length, take: 200, throwOnMoreRecent: true },
    config,
  );
  const allData = [...existingData, ...data];
  if (entireCount > allData.length) {
    return getAllDataFromFetcherImpl<Route, Response>(pageFetcher, config, allData as Response['data']);
  }
  return allData as Response['data'];
};

/**
 * Used to get ALL the data from a particular paginated endpoint
 * and store it in a single array.
 *
 * Generally, this indicates a bad practice, and the design
 * should be reworked to avoid this when possible.
 */
export const getAllDataFromFetcher = async <Route extends EvolveApiRoute<'GET_PAGINATED'>>(
  pageFetcher: PageFetcher<Route>,
  config: AdditionalRequestConfig<'GET_PAGINATED', Route>,
): Promise<EvolveApiResponse<'GET_PAGINATED', Route>['data']> => getAllDataFromFetcherImpl(pageFetcher, config, []);

export const useGetAllDataFromFetcher = <Route extends EvolveApiRoute<'GET_PAGINATED'>>(
  pageFetcher: PageFetcher<Route>,
  initialConfig: AdditionalRequestConfig<'GET_PAGINATED', Route>,
  shouldWait = false,
) => {
  const [data, setData] = useState<EvolveApiResponse<'GET_PAGINATED', Route>['data']>([]);
  const [loading, setLoading] = useState(!shouldWait);
  const [config, setConfig] = useState(initialConfig);
  const [wait, setWait] = useState(shouldWait);
  const [refetchCount, refetch] = useRemountKey('refetch');

  useEffect(() => {
    if (wait) return undefined;
    let cancelled = false;
    setData([]);
    setLoading(true);
    void getAllDataFromFetcher(pageFetcher, config)
      .then((allData) => {
        if (!cancelled) setData(allData);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [config, pageFetcher, wait, /* effect dep */ refetchCount]);

  return [data, loading, { setConfig, setWait, refetch }] as const;
};
